import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import './CarDetailWithInvoices.css';
import quotationService from '../../services/quotationService';
import { formatCarImage, getCarInventory } from '../../utils/carInventoryStorage';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';

interface CarVariant {
  id: number;
  name: string;
  image: string;
  modelName: string;
  price: string;
  financing: string;
  downPayment: string;
  monthlyPayment: string;
  bankName: string;
  roi: string;
  quotationData?: any; // Store the original quotation data
}

interface QuotationData {
  vendor_id: string;
  car_model: string;
  model_variant: string;
  roi_emi_interest: number;
  sbi_bank: number;
  union_bank: number;
  indusind_bank: number;
  au_bank: number;
  ex_showroom: number;
  tcs: number;
  registration: number;
  insurance: number;
  number_plate_crtm_autocard: number;
  gps: number;
  fastag: number;
  speed_governor: number;
  accessories: number;
  on_the_road: number;
  loan_amount: number;
  margin_down_payment: number;
  process_fee: number;
  stamp_duty: number;
  handling_document_charge: number;
  loan_suraksha_insurance: number;
  down_payment: number;
  offers: number;
  final_down_payment: number;
  emi_years: number;
  monthly_emi: number;
  image_url?: string;
}

interface BankOption {
  name: string;
  roi: string;
}

const parseNum = (val: any): number => {
  if (val === null || val === undefined) return 0;
  if (typeof val === 'number') return isNaN(val) ? 0 : val;
  const cleaned = String(val).replace(/[^0-9.-]+/g, '');
  const n = parseFloat(cleaned);
  return isNaN(n) ? 0 : n;
};

const cleanStr = (s: string): string => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

const CarDetailWithInvoices: React.FC = () => {
  const { carId } = useParams<{ carId: string }>();
  const navigate = useNavigate();
  const [selectedVariant, setSelectedVariant] = useState<CarVariant | null>(null);
  const [variants, setVariants] = useState<CarVariant[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedBank, setSelectedBank] = useState<BankOption>({ name: 'State Bank of India', roi: '8.45%' });
  const pageTopRef = useRef<HTMLDivElement>(null);
  const [availableColors, setAvailableColors] = useState<string[]>([]);
  const [quotations, setQuotations] = useState<QuotationData[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [carModel, setCarModel] = useState<string>('');
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [showFullQuotation, setShowFullQuotation] = useState<boolean>(true);
  const invoiceRef = useRef<HTMLDivElement>(null);

  // Bank options - Supports all major banks
  const bankOptions: { [key: string]: BankOption } = {
    'SBI': { name: 'State Bank of India', roi: '8.45%' },
    'UNION': { name: 'Union Bank of India', roi: '8.75%' },
    'INDUSIND': { name: 'IndusInd Bank', roi: '8.95%' },
    'AU': { name: 'AU Small Finance Bank', roi: '9.15%' }
  };

  // Effect to scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'auto'
    });
  }, []);

  // Function to convert car ID to model name - Dynamically supports all brands & models
  const getCarModelFromId = (id: string): string => {
    if (!id) return '';
    const cleanId = id.toLowerCase().trim().replace(/-\d{10,}$/, ''); // Strip timestamp if created via admin

    const idToModelMap: { [key: string]: string } = {
      // Maruti Suzuki
      'maruti-suzuki-wagon-r': 'Maruti Suzuki Wagon-R',
      'wagon-r': 'Maruti Suzuki Wagon-R',
      'wagnor': 'Maruti Suzuki Wagon-R',
      'maruti-suzuki-ertiga': 'Maruti Suzuki ERTIGA',
      'ertiga': 'Maruti Suzuki ERTIGA',
      'maruti-suzuki-dzire': 'Maruti Suzuki Dzire',
      'dzire': 'Maruti Suzuki Dzire',
      'maruti-suzuki-swift': 'Maruti Suzuki Swift',
      'swift': 'Maruti Suzuki Swift',

      // Toyota Models
      'toyota-rumion': 'TOYOTA RUMION',
      'rumion': 'TOYOTA RUMION',
      'toyota-innova-crysta': 'Toyota Innova Crysta',
      'innova-crysta': 'Toyota Innova Crysta',
      'crysta': 'Toyota Innova Crysta',
      'toyota-urban-cruiser-hyryder': 'Toyota Urban Cruiser Hyryder',
      'urban-cruiser-hyryder': 'Toyota Urban Cruiser Hyryder',
      'toyota-hyryder': 'Toyota Urban Cruiser Hyryder',
      'hyryder': 'Toyota Urban Cruiser Hyryder',
      'toyota-innova-hycross': 'Toyota Innova Hycross',
      'innova-hycross': 'Toyota Innova Hycross',
      'toyota-hycross': 'Toyota Innova Hycross',
      'hycross': 'Toyota Innova Hycross',

      // Hyundai Models
      'hyundai-aura': 'HYUNDAI AURA',
      'aura': 'HYUNDAI AURA',
      'hyundai-creta': 'Hyundai Creta',
      'creta': 'Hyundai Creta'
    };

    if (idToModelMap[cleanId]) return idToModelMap[cleanId];
    if (idToModelMap[id.toLowerCase().trim()]) return idToModelMap[id.toLowerCase().trim()];

    // Dynamic search in localStorage inventory
    try {
      const inventory = getCarInventory();
      const matched = inventory.find(c => 
        cleanStr(c.id) === cleanStr(cleanId) || 
        cleanStr(c.id) === cleanStr(id) ||
        cleanStr(c.name) === cleanStr(cleanId)
      );
      if (matched) return matched.name;
    } catch (e) {}

    // Dynamic title case from slug: e.g. "toyota-hycross" -> "Toyota Hycross"
    return cleanId
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Function to get car image path
  const getCarImagePath = (carName: string): string => {
    const c = cleanStr(carName);
    if (c.includes('wagnor') || c.includes('wagonr')) return '/Website-Images/Cars/wagnor.jpg';
    if (c.includes('ertiga')) return '/Website-Images/Cars/ertiga.jpg';
    if (c.includes('rumion') || c.includes('ruminum')) return '/Website-Images/Cars/Ruminum.jpg';
    if (c.includes('aura')) return '/Website-Images/Cars/Aura.jpg';
    if (c.includes('dzire')) return '/Website-Images/Cars/Dzire.jpg';
    if (c.includes('crysta')) return '/Website-Images/Cars/Crysta.jpg';
    if (c.includes('swift')) return '/Website-Images/Cars/wagnor.jpg';
    if (c.includes('creta')) return '/Website-Images/Cars/Crysta.jpg';

    // Check inventory storage
    try {
      const inventory = getCarInventory();
      const matched = inventory.find(item => cleanStr(item.name) === c || c.includes(cleanStr(item.name)));
      if (matched && matched.image) return formatCarImage(matched.image);
    } catch (e) {}

    return '/Website-Images/Cars/placeholder.svg';
  };

  // Function to get bank name from quotation data
  const getBankNameFromQuotation = (quotation: QuotationData): string => {
    if (quotation.sbi_bank > 0) return 'State Bank of India';
    if (quotation.union_bank > 0) return 'Union Bank of India';
    if (quotation.indusind_bank > 0) return 'IndusInd Bank';
    if (quotation.au_bank > 0) return 'AU Small Finance Bank';
    return 'State Bank of India';
  };

  // Function to generate realistic fallback quotation data if DB has none for this model
  const generateFallbackQuotationsForModel = (modelName: string): QuotationData[] => {
    const c = cleanStr(modelName);

    // 1. Toyota Hyryder
    if (c.includes('hyryder')) {
      return [
        {
          vendor_id: 'V-HYRYDER-SCNG',
          car_model: modelName,
          model_variant: 'S CNG 1.5 MT',
          roi_emi_interest: 9.65,
          sbi_bank: 85,
          union_bank: 0,
          indusind_bank: 0,
          au_bank: 0,
          ex_showroom: 1381000,
          tcs: 13810,
          registration: 98000,
          insurance: 42000,
          number_plate_crtm_autocard: 2500,
          gps: 6000,
          fastag: 500,
          speed_governor: 1000,
          accessories: 15000,
          on_the_road: 1558810,
          loan_amount: 1300000,
          margin_down_payment: 258810,
          process_fee: 5500,
          stamp_duty: 3500,
          handling_document_charge: 8000,
          loan_suraksha_insurance: 24000,
          down_payment: 299810,
          offers: 30000,
          final_down_payment: 269810,
          emi_years: 5,
          monthly_emi: 27415,
          image_url: '/Website-Images/Cars/placeholder.svg'
        },
        {
          vendor_id: 'V-HYRYDER-GCNG',
          car_model: modelName,
          model_variant: 'G CNG 1.5 MT',
          roi_emi_interest: 9.65,
          sbi_bank: 85,
          union_bank: 0,
          indusind_bank: 0,
          au_bank: 0,
          ex_showroom: 1559000,
          tcs: 15590,
          registration: 110000,
          insurance: 48000,
          number_plate_crtm_autocard: 2500,
          gps: 6000,
          fastag: 500,
          speed_governor: 1000,
          accessories: 18000,
          on_the_road: 1760090,
          loan_amount: 1470000,
          margin_down_payment: 290090,
          process_fee: 6000,
          stamp_duty: 4000,
          handling_document_charge: 9000,
          loan_suraksha_insurance: 26000,
          down_payment: 335090,
          offers: 35000,
          final_down_payment: 300090,
          emi_years: 5,
          monthly_emi: 31000,
          image_url: '/Website-Images/Cars/placeholder.svg'
        }
      ];
    }

    // 2. Toyota Hycross
    if (c.includes('hycross')) {
      return [
        {
          vendor_id: 'V-HYCROSS-GX',
          car_model: modelName,
          model_variant: 'GX 7STR Petrol',
          roi_emi_interest: 9.65,
          sbi_bank: 85,
          union_bank: 0,
          indusind_bank: 0,
          au_bank: 0,
          ex_showroom: 1977000,
          tcs: 19770,
          registration: 135000,
          insurance: 58000,
          number_plate_crtm_autocard: 3000,
          gps: 8000,
          fastag: 500,
          speed_governor: 1500,
          accessories: 25000,
          on_the_road: 2227770,
          loan_amount: 1850000,
          margin_down_payment: 377770,
          process_fee: 8000,
          stamp_duty: 5000,
          handling_document_charge: 12000,
          loan_suraksha_insurance: 35000,
          down_payment: 437770,
          offers: 40000,
          final_down_payment: 397770,
          emi_years: 5,
          monthly_emi: 38950,
          image_url: '/Website-Images/Cars/placeholder.svg'
        },
        {
          vendor_id: 'V-HYCROSS-VX',
          car_model: modelName,
          model_variant: 'VX 7STR Hybrid',
          roi_emi_interest: 9.65,
          sbi_bank: 85,
          union_bank: 0,
          indusind_bank: 0,
          au_bank: 0,
          ex_showroom: 2597000,
          tcs: 25970,
          registration: 165000,
          insurance: 75000,
          number_plate_crtm_autocard: 3000,
          gps: 8000,
          fastag: 500,
          speed_governor: 1500,
          accessories: 30000,
          on_the_road: 2905970,
          loan_amount: 2420000,
          margin_down_payment: 485970,
          process_fee: 10000,
          stamp_duty: 6500,
          handling_document_charge: 15000,
          loan_suraksha_insurance: 42000,
          down_payment: 559470,
          offers: 50000,
          final_down_payment: 509470,
          emi_years: 5,
          monthly_emi: 50950,
          image_url: '/Website-Images/Cars/placeholder.svg'
        }
      ];
    }

    // 3. Toyota Rumion
    if (c.includes('rumion')) {
      return [
        {
          vendor_id: 'V-RUMION-SCNG',
          car_model: modelName,
          model_variant: 'S CNG 1.5 MT',
          roi_emi_interest: 9.65,
          sbi_bank: 85,
          union_bank: 0,
          indusind_bank: 0,
          au_bank: 0,
          ex_showroom: 1161500,
          tcs: 11615,
          registration: 82000,
          insurance: 34000,
          number_plate_crtm_autocard: 2500,
          gps: 6000,
          fastag: 500,
          speed_governor: 1000,
          accessories: 12000,
          on_the_road: 1283115,
          loan_amount: 1071098,
          margin_down_payment: 212017,
          process_fee: 5000,
          stamp_duty: 3000,
          handling_document_charge: 7000,
          loan_suraksha_insurance: 20000,
          down_payment: 247017,
          offers: 22500,
          final_down_payment: 224517,
          emi_years: 5,
          monthly_emi: 22574,
          image_url: '/Website-Images/Cars/Ruminum.jpg'
        }
      ];
    }

    // Dynamic fallback from inventory
    try {
      const inventory = getCarInventory();
      const matched = inventory.find(item => cleanStr(item.name) === c || c.includes(cleanStr(item.name)));
      if (matched && matched.variants && matched.variants.length > 0) {
        return matched.variants.map((v, i) => {
          const ex = v.exShowroomPrice || 850000;
          const onRoad = v.onRoadPrice || Math.round(ex * 1.15);
          const loan = Math.round(onRoad * 0.85);
          const dp = Math.round(onRoad - loan + 25000);
          return {
            vendor_id: `V-${c}-${i}`,
            car_model: modelName,
            model_variant: v.name,
            roi_emi_interest: 9.65,
            sbi_bank: 85,
            union_bank: 0,
            indusind_bank: 0,
            au_bank: 0,
            ex_showroom: ex,
            tcs: ex > 1000000 ? Math.round(ex * 0.01) : 0,
            registration: Math.round(ex * 0.08),
            insurance: Math.round(ex * 0.035),
            number_plate_crtm_autocard: 2500,
            gps: 6000,
            fastag: 500,
            speed_governor: 1000,
            accessories: 10000,
            on_the_road: onRoad,
            loan_amount: loan,
            margin_down_payment: Math.round(onRoad - loan),
            process_fee: 5000,
            stamp_duty: 3000,
            handling_document_charge: 7000,
            loan_suraksha_insurance: 15000,
            down_payment: dp,
            offers: 15000,
            final_down_payment: dp - 15000,
            emi_years: 5,
            monthly_emi: Math.round(loan * 0.021),
            image_url: matched.image || '/Website-Images/Cars/placeholder.svg'
          };
        });
      }
    } catch (e) {}

    return [];
  };

  // Function to convert quotation to CarVariant
  const convertQuotationToVariant = (quotation: QuotationData, index: number): CarVariant => {
    const bankName = getBankNameFromQuotation(quotation);
    const imagePath = quotation.image_url ? formatCarImage(quotation.image_url) : getCarImagePath(quotation.car_model);
    const carModelNamePart = quotation.car_model.split(' ').slice(-1)[0] || quotation.car_model;
    const variantNamePart = quotation.model_variant || 'Standard';
    
    return {
      id: index + 1,
      name: `${carModelNamePart} ${variantNamePart}`,
      image: imagePath,
      modelName: `${carModelNamePart.toUpperCase()} ${variantNamePart.toUpperCase()}`,
      price: `₹${(quotation.ex_showroom || 0).toLocaleString('en-IN')}`,
      financing: bankName,
      downPayment: `₹${(quotation.final_down_payment || quotation.down_payment || 0).toLocaleString('en-IN')}`,
      monthlyPayment: `₹${Math.round(quotation.monthly_emi || 0).toLocaleString('en-IN')}`,
      bankName: bankName,
      roi: `${quotation.roi_emi_interest || 8.45}%`,
      quotationData: quotation
    };
  };

  // Fetch car data from backend
  useEffect(() => {
    const fetchCarData = async () => {
      if (!carId) {
        navigate('/');
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        
        const modelName = getCarModelFromId(carId);
        if (!modelName) {
          setError('Car model not found');
          navigate('/');
          return;
        }

        setCarModel(modelName);
        console.log(`Fetching quotations for: ${modelName}`);

        // Get all quotations with robust case-insensitive and bank-agnostic matching
        let matchingQuotations: QuotationData[] = [];
        try {
          const allQuotations = await quotationService.getAllQuotations();
          const targetClean = cleanStr(modelName);

          matchingQuotations = (allQuotations || []).filter((q: QuotationData) => {
            const qClean = cleanStr(q.car_model);
            const matchesModel = qClean === targetClean || 
                                 qClean.includes(targetClean) || 
                                 targetClean.includes(qClean);
            // Allow quotations with SBI or any valid financial data (supports both T-permit & Private)
            const hasPricing = (q.sbi_bank > 0) || (q.union_bank > 0) || (q.indusind_bank > 0) || (q.au_bank > 0) || (q.final_down_payment > 0) || (q.monthly_emi > 0) || (q.ex_showroom > 0);
            return matchesModel && hasPricing;
          });
        } catch (fetchErr) {
          console.warn('Backend quotations fetch failed, falling back to local specifications:', fetchErr);
        }
        
        // Zero-Bounce Guarantee: If DB returns no quotations, load curated fallback data
        if (matchingQuotations.length === 0) {
          console.log(`No database quotations found for ${modelName}, using fallback specifications.`);
          matchingQuotations = generateFallbackQuotationsForModel(modelName);
        }

        if (matchingQuotations.length === 0) {
          setError('No quotation details found for this car model');
          setIsLoading(false);
          return;
        }

        setQuotations(matchingQuotations);

        // Convert quotations to CarVariant format
        const carVariants = matchingQuotations.map((quotation: QuotationData, index: number) => 
          convertQuotationToVariant(quotation, index)
        );

        // Group by variant name and select best offer for each variant
        const variantGroups: { [key: string]: CarVariant[] } = {};
        carVariants.forEach(variant => {
          const variantKey = variant.name;
          if (!variantGroups[variantKey]) {
            variantGroups[variantKey] = [];
          }
          variantGroups[variantKey].push(variant);
        });

        // Select the best variant from each group (lowest down payment)
        const bestVariants = Object.values(variantGroups).map(group => {
          return group.reduce((best, current) => {
            const bestDownPayment = parseFloat(best.downPayment.replace(/[₹,]/g, '')) || 0;
            const currentDownPayment = parseFloat(current.downPayment.replace(/[₹,]/g, '')) || 0;
            return (currentDownPayment < bestDownPayment && currentDownPayment > 0) ? current : best;
          });
        });

        setVariants(bestVariants);
        
        // Set the first variant as selected
        if (bestVariants.length > 0) {
          setSelectedVariant(bestVariants[0]);
          setSelectedBank(bankOptions['SBI']);
        }

      } catch (error) {
        console.error('Error fetching car data:', error);
        setError('Failed to load car data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCarData();
  }, [carId, navigate]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleVariantSelect = (variant: CarVariant) => {
    setSelectedVariant(variant);

    // Always set SBI bank since we only deal with SBI quotations
    setSelectedBank(bankOptions['SBI']);

    scrollToTop();
  };

  const handleBankSelect = (bankKey: string) => {
    // Only SBI bank is supported, so no bank switching needed
    if (bankKey === 'SBI') {
      setSelectedBank(bankOptions['SBI']);
    }
  };

  const toggleFullQuotation = () => {
    setShowFullQuotation(!showFullQuotation);
  };

  const downloadAsPDF = async () => {
    if (!selectedVariant || !invoiceRef.current) return;
    
    try {
      setIsDownloading(true);
      
      // Temporarily show full quotation for PDF generation
      const wasShowingFull = showFullQuotation;
      setShowFullQuotation(true);
      
      // Wait for DOM update
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const canvas = await html2canvas(invoiceRef.current, {
        scale: 3, // Increased scale for better quality
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        width: invoiceRef.current.offsetWidth,
        height: invoiceRef.current.offsetHeight,
        onclone: (clonedDoc) => {
          const clonedElement = clonedDoc.querySelector('.invoice-content');
          const companyHeader = clonedDoc.querySelector('.company-header');
          const companyHeaderH2 = clonedDoc.querySelector('.company-header h2');
          const companyDetails = clonedDoc.querySelectorAll('.company-details p');
          const tableElement = clonedDoc.querySelector('.invoice-table');
          const tableCells = clonedDoc.querySelectorAll('.invoice-table td');
          
          if (clonedElement) {
            (clonedElement as HTMLElement).style.backgroundColor = '#ffffff';
            (clonedElement as HTMLElement).style.padding = '20px';
            (clonedElement as HTMLElement).style.fontSize = '16px';
            (clonedElement as HTMLElement).style.lineHeight = '1.5';
            (clonedElement as HTMLElement).style.fontFamily = 'Arial, sans-serif';
            (clonedElement as HTMLElement).style.color = '#000000'; // Ensure black text
          }
          
          // Force display and style company header for PDF
          if (companyHeader) {
            (companyHeader as HTMLElement).style.display = 'block';
            (companyHeader as HTMLElement).style.textAlign = 'center';
            (companyHeader as HTMLElement).style.padding = '20px 0';
            (companyHeader as HTMLElement).style.borderBottom = '2px solid #e5e7eb';
            (companyHeader as HTMLElement).style.marginBottom = '20px';
            (companyHeader as HTMLElement).style.backgroundColor = '#ffffff';
          }
          
          if (companyHeaderH2) {
            (companyHeaderH2 as HTMLElement).style.fontSize = '24px';
            (companyHeaderH2 as HTMLElement).style.fontWeight = '700';
            (companyHeaderH2 as HTMLElement).style.color = '#000000'; // Pure black for better visibility
            (companyHeaderH2 as HTMLElement).style.margin = '0 0 10px 0';
            (companyHeaderH2 as HTMLElement).style.textTransform = 'uppercase';
            (companyHeaderH2 as HTMLElement).style.letterSpacing = '1px';
            (companyHeaderH2 as HTMLElement).style.fontFamily = 'Arial, sans-serif';
          }
          
          // Style company details
          companyDetails.forEach(detail => {
            (detail as HTMLElement).style.margin = '4px 0';
            (detail as HTMLElement).style.fontSize = '12px';
            (detail as HTMLElement).style.color = '#000000'; // Pure black for better visibility
            (detail as HTMLElement).style.fontWeight = '600'; // Bolder text
            (detail as HTMLElement).style.lineHeight = '1.4';
            (detail as HTMLElement).style.fontFamily = 'Arial, sans-serif';
          });
          
          // Improve table styling for PDF
          if (tableElement) {
            (tableElement as HTMLElement).style.fontSize = '14px';
            (tableElement as HTMLElement).style.width = '100%';
            (tableElement as HTMLElement).style.borderCollapse = 'collapse';
            (tableElement as HTMLElement).style.fontFamily = 'Arial, sans-serif';
            (tableElement as HTMLElement).style.backgroundColor = '#ffffff';
            (tableElement as HTMLElement).style.color = '#000000';
          }
          
          // Style specific table rows for better visibility
          const tableRows = clonedDoc.querySelectorAll('.invoice-table tr');
          tableRows.forEach((row, index) => {
            if (index % 2 === 0) {
              (row as HTMLElement).style.backgroundColor = '#f8f9fa';
            } else {
              (row as HTMLElement).style.backgroundColor = '#ffffff';
            }
          });
          
          // Style important rows with better contrast
          const onRoadRow = clonedDoc.querySelector('.on-road-row');
          if (onRoadRow) {
            (onRoadRow as HTMLElement).style.backgroundColor = '#e3f2fd !important';
            (onRoadRow as HTMLElement).style.fontWeight = '700';
          }
          
          const emiRow = clonedDoc.querySelector('.emi-row');
          if (emiRow) {
            (emiRow as HTMLElement).style.backgroundColor = '#fff3e0 !important';
            (emiRow as HTMLElement).style.fontWeight = '700';
          }
          
          // Enhance table cell styling for better readability
          tableCells.forEach(cell => {
            (cell as HTMLElement).style.padding = '12px 16px';
            (cell as HTMLElement).style.fontSize = '14px';
            (cell as HTMLElement).style.lineHeight = '1.4';
            (cell as HTMLElement).style.color = '#000000'; // Pure black for better contrast
            (cell as HTMLElement).style.backgroundColor = '#ffffff'; // Pure white background
            (cell as HTMLElement).style.border = '1px solid #000000'; // Black borders
            (cell as HTMLElement).style.fontFamily = 'Arial, sans-serif';
            (cell as HTMLElement).style.fontWeight = '600'; // Bolder text
          });
        }
      });

      const imgData = canvas.toDataURL('image/png', 1.0); // Maximum quality
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Calculate image dimensions and position
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const maxWidth = pdfWidth - 20; // 10mm margin on each side
      const maxHeight = pdfHeight - 30; // 15mm margin top and bottom
      const ratio = Math.min(maxWidth / imgWidth, maxHeight / imgHeight);
      
      const scaledWidth = imgWidth * ratio;
      const scaledHeight = imgHeight * ratio;
      const imgX = (pdfWidth - scaledWidth) / 2;
      const imgY = 15; // Small top margin

      pdf.addImage(imgData, 'PNG', imgX, imgY, scaledWidth, scaledHeight, undefined, 'FAST');
      
      const fileName = `ASW_Quotation_${selectedVariant.modelName.replace(/\s+/g, '_')}_${selectedVariant.bankName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
      // Restore previous quotation state
      setShowFullQuotation(wasShowingFull);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadAsExcel = () => {
    if (!selectedVariant) return;
    
    try {
      setIsDownloading(true);
      
      const quotationData = selectedVariant.quotationData;
      
      // TCS based on quotation data
      const exShowroom = quotationData?.ex_showroom || 0;
      const calculatedTCS = parseNum(quotationData?.tcs || 0);
      const showTCS = calculatedTCS > 0;
      
      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet: any = {};
      
      // Company header with styling
      worksheet['A1'] = { 
        v: 'ASW CARS & FINANCE', 
        t: 's',
        s: {
          font: { bold: true, color: { rgb: 'FFFFFF' }, size: 18 },
          fill: { fgColor: { rgb: '2563EB' } },
          alignment: { horizontal: 'center', vertical: 'center' }
        }
      };
      worksheet['B1'] = { 
        v: '', 
        s: {
          fill: { fgColor: { rgb: '2563EB' } }
        }
      };
      
      // Merge company header across columns
      if (!worksheet['!merges']) worksheet['!merges'] = [];
      worksheet['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } });
      
      // Add empty row
      worksheet['A2'] = { v: '', t: 's' };
      worksheet['B2'] = { v: '', t: 's' };
      
      // Vehicle details section with blue header
      const detailsStartRow = 2;
      worksheet[`A${detailsStartRow + 1}`] = { 
        v: 'Car Model', 
        t: 's',
        s: {
          font: { bold: true, color: { rgb: 'FFFFFF' } },
          fill: { fgColor: { rgb: '3B82F6' } },
          alignment: { horizontal: 'left', vertical: 'center' }
        }
      };
      worksheet[`B${detailsStartRow + 1}`] = { 
        v: selectedVariant.modelName, 
        t: 's',
        s: {
          font: { bold: true },
          fill: { fgColor: { rgb: 'DBEAFE' } },
          alignment: { horizontal: 'left', vertical: 'center' }
        }
      };
      
      worksheet[`A${detailsStartRow + 2}`] = { 
        v: 'Bank', 
        t: 's',
        s: {
          font: { bold: true, color: { rgb: 'FFFFFF' } },
          fill: { fgColor: { rgb: '3B82F6' } },
          alignment: { horizontal: 'left', vertical: 'center' }
        }
      };
      worksheet[`B${detailsStartRow + 2}`] = { 
        v: selectedVariant.bankName, 
        t: 's',
        s: {
          fill: { fgColor: { rgb: 'DBEAFE' } },
          alignment: { horizontal: 'left', vertical: 'center' }
        }
      };
      
      worksheet[`A${detailsStartRow + 3}`] = { 
        v: 'ROI', 
        t: 's',
        s: {
          font: { bold: true, color: { rgb: 'FFFFFF' } },
          fill: { fgColor: { rgb: '3B82F6' } },
          alignment: { horizontal: 'left', vertical: 'center' }
        }
      };
      worksheet[`B${detailsStartRow + 3}`] = { 
        v: selectedVariant.roi, 
        t: 's',
        s: {
          font: { bold: true, color: { rgb: '1E40AF' } },
          fill: { fgColor: { rgb: 'E0E7FF' } },
          alignment: { horizontal: 'left', vertical: 'center' }
        }
      };
      
      // Empty row
      worksheet[`A${detailsStartRow + 4}`] = { v: '', t: 's' };
      worksheet[`B${detailsStartRow + 4}`] = { v: '', t: 's' };
      
      // Pricing section with green theme
      let currentRow = detailsStartRow + 5;
      
      // Ex-Showroom Price
      worksheet[`A${currentRow}`] = { 
        v: 'Cost of the vehicle EX Showroom', 
        t: 's',
        s: {
          font: { bold: true, color: { rgb: 'FFFFFF' } },
          fill: { fgColor: { rgb: '059669' } },
          alignment: { horizontal: 'left', vertical: 'center' }
        }
      };
      worksheet[`B${currentRow}`] = { 
        v: `₹${exShowroom.toLocaleString('en-IN')}`, 
        t: 's',
        s: {
          font: { bold: true },
          fill: { fgColor: { rgb: 'DCFCE7' } },
          alignment: { horizontal: 'right', vertical: 'center' }
        }
      };
      currentRow++;
      
      // TCS if applicable
      if (showTCS) {
        worksheet[`A${currentRow}`] = { 
          v: 'TCS', 
          t: 's',
          s: {
            font: { bold: true },
            fill: { fgColor: { rgb: 'FEF3C7' } },
            alignment: { horizontal: 'left', vertical: 'center' }
          }
        };
        worksheet[`B${currentRow}`] = { 
          v: `₹${Math.round(calculatedTCS).toLocaleString('en-IN')}`, 
          t: 's',
          s: {
            fill: { fgColor: { rgb: 'FDE68A' } },
            alignment: { horizontal: 'right', vertical: 'center' }
          }
        };
        currentRow++;
      }
      
      // Other charges
      const charges = [
        ['Registration', quotationData?.registration || 39500],
        ['Insurance', quotationData?.insurance || 39500],
        ['GPS', quotationData?.gps || 18500],
        ['Speed Governor', quotationData?.speed_governor || 0]
      ];
      
      charges.forEach(([label, value]) => {
        worksheet[`A${currentRow}`] = { 
          v: label, 
          t: 's',
          s: {
            fill: { fgColor: { rgb: 'F3F4F6' } },
            alignment: { horizontal: 'left', vertical: 'center' }
          }
        };
        worksheet[`B${currentRow}`] = { 
          v: `₹${value.toLocaleString('en-IN')}`, 
          t: 's',
          s: {
            fill: { fgColor: { rgb: 'F9FAFB' } },
            alignment: { horizontal: 'right', vertical: 'center' }
          }
        };
        currentRow++;
      });
      
      // On the road price - highlighted
      const onRoadTotal = quotationData?.on_the_road !== undefined && quotationData?.on_the_road !== null && quotationData?.on_the_road !== ''
        ? parseNum(quotationData.on_the_road)
        : (exShowroom + calculatedTCS + 
          (quotationData?.registration || 0) + 
          (quotationData?.insurance || 0) + 
          (quotationData?.gps || 0) + 
          (quotationData?.speed_governor || 0) + 
          (quotationData?.accessories || 0));
      
      worksheet[`A${currentRow}`] = { 
        v: 'On the road', 
        t: 's',
        s: {
          font: { bold: true, color: { rgb: 'FFFFFF' } },
          fill: { fgColor: { rgb: '22C55E' } },
          alignment: { horizontal: 'left', vertical: 'center' }
        }
      };
      worksheet[`B${currentRow}`] = { 
        v: `₹${Math.round(onRoadTotal).toLocaleString('en-IN')}`, 
        t: 's',
        s: {
          font: { bold: true, size: 14, color: { rgb: '166534' } },
          fill: { fgColor: { rgb: 'BBF7D0' } },
          alignment: { horizontal: 'right', vertical: 'center' }
        }
      };
      currentRow++;
      
      // Loan details section with orange theme
      const loanDetails = [
        ['Loan Amount', quotationData?.loan_amount || 1010000],
        ['Margin (Down payment)', quotationData?.margin_down_payment || 201105],
        ['Process fee', quotationData?.process_fee || 16500],
        ['Stamp duty', quotationData?.stamp_duty || 6500],
        ['Handling Loan Services and Document Charge', quotationData?.handling_document_charge || 30300]
      ];
      
      loanDetails.forEach(([label, value]) => {
        worksheet[`A${currentRow}`] = { 
          v: label, 
          t: 's',
          s: {
            font: { bold: true, color: { rgb: 'FFFFFF' } },
            fill: { fgColor: { rgb: 'F97316' } },
            alignment: { horizontal: 'left', vertical: 'center' }
          }
        };
        worksheet[`B${currentRow}`] = { 
          v: `₹${value.toLocaleString('en-IN')}`, 
          t: 's',
          s: {
            fill: { fgColor: { rgb: 'FED7AA' } },
            alignment: { horizontal: 'right', vertical: 'center' }
          }
        };
        currentRow++;
      });
      
      // Down Payment - highlighted
      worksheet[`A${currentRow}`] = { 
        v: 'Down Payment', 
        t: 's',
        s: {
          font: { bold: true, color: { rgb: 'FFFFFF' } },
          fill: { fgColor: { rgb: '3B82F6' } },
          alignment: { horizontal: 'left', vertical: 'center' }
        }
      };
      worksheet[`B${currentRow}`] = { 
        v: `₹${(quotationData?.down_payment || 0).toLocaleString('en-IN')}`, 
        t: 's',
        s: {
          font: { bold: true, size: 14, color: { rgb: '1E40AF' } },
          fill: { fgColor: { rgb: 'BFDBFE' } },
          alignment: { horizontal: 'right', vertical: 'center' }
        }
      };
      currentRow++;
      
      // Offers - highlighted in pink
      worksheet[`A${currentRow}`] = { 
        v: 'Offers', 
        t: 's',
        s: {
          font: { bold: true, color: { rgb: 'FFFFFF' } },
          fill: { fgColor: { rgb: 'EC4899' } },
          alignment: { horizontal: 'left', vertical: 'center' }
        }
      };
      worksheet[`B${currentRow}`] = { 
        v: `₹${(quotationData?.offers || 0).toLocaleString('en-IN')}`, 
        t: 's',
        s: {
          font: { bold: true, color: { rgb: 'BE185D' } },
          fill: { fgColor: { rgb: 'FBCFE8' } },
          alignment: { horizontal: 'right', vertical: 'center' }
        }
      };
      currentRow++;
      
      // Final Down Payment - highlighted
      worksheet[`A${currentRow}`] = { 
        v: 'Final Down Payment', 
        t: 's',
        s: {
          font: { bold: true, color: { rgb: 'FFFFFF' } },
          fill: { fgColor: { rgb: '7C3AED' } },
          alignment: { horizontal: 'left', vertical: 'center' }
        }
      };
      worksheet[`B${currentRow}`] = { 
        v: `₹${(quotationData?.final_down_payment || 0).toLocaleString('en-IN')}`, 
        t: 's',
        s: {
          font: { bold: true, size: 14, color: { rgb: '5B21B6' } },
          fill: { fgColor: { rgb: 'DDD6FE' } },
          alignment: { horizontal: 'right', vertical: 'center' }
        }
      };
      currentRow++;
      
      // EMI - highlighted in gold
      worksheet[`A${currentRow}`] = { 
        v: `${quotationData?.emi_years || 5} years (${(quotationData?.emi_years || 5) * 12} Months)`, 
        t: 's',
        s: {
          font: { bold: true, color: { rgb: 'FFFFFF' } },
          fill: { fgColor: { rgb: 'D97706' } },
          alignment: { horizontal: 'left', vertical: 'center' }
        }
      };
      worksheet[`B${currentRow}`] = { 
        v: `₹${Math.round(quotationData?.monthly_emi || 0).toLocaleString('en-IN')}`, 
        t: 's',
        s: {
          font: { bold: true, size: 16, color: { rgb: 'DC2626' } },
          fill: { fgColor: { rgb: 'FEF3C7' } },
          alignment: { horizontal: 'right', vertical: 'center' }
        }
      };
      
      // Set column widths
      worksheet['!cols'] = [
        { wch: 40 },
        { wch: 25 }
      ];
      
      // Set row heights
      worksheet['!rows'] = [];
      for (let i = 0; i <= currentRow; i++) {
        worksheet['!rows'][i] = { hpx: i === 0 ? 30 : 25 };
      }
      
      // Set worksheet range
      worksheet['!ref'] = `A1:B${currentRow}`;
      
      XLSX.utils.book_append_sheet(workbook, worksheet, 'ASW Quotation');
      
      const fileName = `ASW_Quotation_${selectedVariant.modelName.replace(/\s+/g, '_')}_${selectedVariant.bankName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      
    } catch (error) {
      console.error('Error generating Excel:', error);
      alert('Failed to generate Excel file. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  

  // Add loading and error states
  if (error) {
    return (
      <div className="error-page" style={{ 
        padding: '60px 20px', 
        textAlign: 'center',
        maxWidth: '600px',
        margin: '0 auto'
      }}>
        <h2 style={{ color: '#ef4444', marginBottom: '16px' }}>⚠️ Error Loading Car Data</h2>
        <p style={{ color: '#64748b', marginBottom: '24px' }}>{error}</p>
        <button 
          onClick={() => navigate('/')}
          style={{
            padding: '12px 24px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          Back to Home
        </button>
      </div>
    );
  }

  if (isLoading || !selectedVariant) {
    return (
      <div className="loading-page" style={{ 
        padding: '60px 20px', 
        textAlign: 'center',
        color: '#64748b'
      }}>
        <div className="loading-spinner" style={{
          display: 'inline-block',
          width: '40px',
          height: '40px',
          border: '3px solid #e2e8f0',
          borderTop: '3px solid #3b82f6',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '16px'
        }}></div>
        <p>Loading car details...</p>
      </div>
    );
  }

  return (
    <div className="car-detail-page" ref={pageTopRef}>
      {isLoading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}
      
      {/* ── Compact Breadcrumb Navigation Bar ── */}
      <div className="car-detail-breadcrumb">
        <Link to="/" className="breadcrumb-back-btn">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          <span>Back to All Cars</span>
        </Link>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">{selectedVariant.name} Price Breakup</span>
      </div>

      <div className="car-detail-container">
        {/* ── Left Column: Sticky Vehicle Overview ── */}
        <div className="car-image-section">
          <div className="car-image-card">
            <img src={selectedVariant.image} alt={selectedVariant.name} className="car-detail-image" />
          </div>

          <div className="car-title-block">
            <h1 className="car-name">{selectedVariant.name}</h1>
            <div className="car-feature-pills">
              <span className="feature-pill"><i className="fa-solid fa-gas-pump"></i> CNG / Petrol</span>
              <span className="feature-pill"><i className="fa-solid fa-shield-halved"></i> 100% Funding</span>
            </div>
          </div>
          
          {/* Variants Selector */}
          <div className="car-variants-section-inline">
            <h3 className="variants-heading">Select Model / Variant ({variants.length})</h3>
            <div className="car-variants-grid-inline">
              {variants.map(variant => (
                <div 
                  key={variant.id}
                  className={`variant-card-inline ${selectedVariant.id === variant.id ? 'active' : ''}`}
                  onClick={() => handleVariantSelect(variant)}
                >
                  <div className="variant-image-container-inline">
                    <img src={variant.image} alt={variant.name} />
                  </div>
                  <div className="variant-details-inline">
                    <h4 className="variant-title">{variant.name}</h4>
                    <div className="variant-price-row">
                      <span className="variant-downpayment">DP: ₹{parseNum(variant.downPayment).toLocaleString('en-IN')}</span>
                      <span className="variant-emi">EMI: ₹{parseNum(variant.monthlyPayment).toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* ── Right Column: Quotation & Price Breakdown ── */}
        <div className="invoice-section">
          {/* Header Row: Dealership Branding + Financing Partner SBI */}
          <div className="invoice-header-row">
            <div className="invoice-brand-info">
              <h2 className="invoice-brand-title">ASW CARS & FINANCE</h2>
              <span className="invoice-brand-sub">Official On-Road Price & EMI Quotation</span>
            </div>

            <div className="invoice-bank-pill">
              <span className="bank-pill-label">Finance Partner</span>
              <div className="bank-pill-content">
                <img 
                  src="/Website-Images/Banks/SBI.webp" 
                  alt="SBI" 
                  className="bank-pill-logo"
                />
                <span className="bank-pill-name">State Bank of India</span>
                <span className="bank-pill-roi">ROI {selectedVariant.roi || '9.65%'}</span>
              </div>
            </div>
          </div>

          {/* Compact Ex-Showroom Price Banner */}
          <div className="ex-showroom-banner">
            <div className="ex-showroom-left">
              <span className="ex-label">Ex-Showroom Price</span>
              <span className="ex-amount">
                ₹{parseNum(selectedVariant.quotationData?.ex_showroom || selectedVariant.price).toLocaleString('en-IN')}
              </span>
            </div>
            <div className="ex-showroom-right">
              <span className="ex-tag">T-Permit & Commercial Ready</span>
            </div>
          </div>
          
          {/* Full Quotation Table */}
          <div className="quotation-container">
            <div className="invoice-content" ref={invoiceRef}>
              {/* Company Header for PDF */}
              <div className="company-header">
                <h2>ASW CARS & FINANCE</h2>
                <div className="company-details">
                  <p>Shop No. 123, Main Road, Auto Market</p>
                  <p>City Center, Thane - 400607</p>
                  <p>Contact: +91 99878 28690 | Email: asw.cars@gmail.com</p>
                </div>
              </div>

              <table className="invoice-table">
                <tbody>
                  <tr className="invoice-model-row">
                    <td>Car Model / Variant</td>
                    <td className="highlight-cell">{selectedVariant.modelName || selectedVariant.name}</td>
                  </tr>
                  <tr>
                    <td>Financing Bank</td>
                    <td>{selectedVariant.bankName || 'State Bank of India'}</td>
                  </tr>
                  <tr className="roi-row">
                    <td>Rate of Interest (ROI)</td>
                    <td>ROI {selectedVariant.roi || '9.65%'}</td>
                  </tr>
                  <tr>
                    <td>Cost of the Vehicle EX Showroom</td>
                    <td>₹{parseNum(selectedVariant.quotationData?.ex_showroom || selectedVariant.price).toLocaleString('en-IN')}</td>
                  </tr>

                  {/* TCS from quotation */}
                  {(() => {
                    const tcsVal = parseNum(selectedVariant.quotationData?.tcs);
                    if (tcsVal > 0) {
                      return (
                        <tr>
                          <td>TCS</td>
                          <td>₹{tcsVal.toLocaleString('en-IN')}</td>
                        </tr>
                      );
                    }
                    return null;
                  })()}

                  <tr>
                    <td>Registration (RTO)</td>
                    <td>₹{parseNum(selectedVariant.quotationData?.registration || 35500).toLocaleString('en-IN')}</td>
                  </tr>
                  <tr>
                    <td>Insurance (1st Year Comprehensive)</td>
                    <td>₹{parseNum(selectedVariant.quotationData?.insurance || 34500).toLocaleString('en-IN')}</td>
                  </tr>
                  <tr>
                    <td>GPS & AIS-140 Device</td>
                    <td>₹{parseNum(selectedVariant.quotationData?.gps || 17500).toLocaleString('en-IN')}</td>
                  </tr>
                  <tr>
                    <td>Speed Governor</td>
                    <td>₹{parseNum(selectedVariant.quotationData?.speed_governor || 0).toLocaleString('en-IN')}</td>
                  </tr>

                  {/* On-Road Price Row */}
                  <tr className="on-road-row highlighted-field">
                    <td><strong>On The Road Price</strong></td>
                    <td>
                      <strong>
                        ₹{(() => {
                          const q = selectedVariant.quotationData;
                          if (q && q.on_the_road !== undefined && q.on_the_road !== null && q.on_the_road !== '') {
                            return parseNum(q.on_the_road).toLocaleString('en-IN');
                          }
                          const exShowroom = parseNum(q?.ex_showroom || selectedVariant.price);
                          const tcs = parseNum(q?.tcs || 0);
                          const total = 
                            exShowroom + 
                            tcs + 
                            parseNum(q?.registration || 35500) + 
                            parseNum(q?.insurance || 34500) + 
                            parseNum(q?.gps || 17500) + 
                            parseNum(q?.speed_governor || 0) + 
                            parseNum(q?.accessories || 0);

                          return Math.round(total).toLocaleString('en-IN');
                        })()}
                      </strong>
                    </td>
                  </tr>

                  <tr>
                    <td>Loan Amount Financed</td>
                    <td>₹{parseNum(selectedVariant.quotationData?.loan_amount || 645586).toLocaleString('en-IN')}</td>
                  </tr>
                  <tr>
                    <td>Margin (Down Payment Margin)</td>
                    <td>₹{parseNum(selectedVariant.quotationData?.margin_down_payment || 163714).toLocaleString('en-IN')}</td>
                  </tr>
                  <tr>
                    <td>Bank Processing Fee</td>
                    <td>₹{parseNum(selectedVariant.quotationData?.process_fee || 15500).toLocaleString('en-IN')}</td>
                  </tr>
                  <tr>
                    <td>Stamp Duty</td>
                    <td>₹{parseNum(selectedVariant.quotationData?.stamp_duty || 6500).toLocaleString('en-IN')}</td>
                  </tr>
                  <tr>
                    <td>Handling Loan Services & Documentation</td>
                    <td>₹{parseNum(selectedVariant.quotationData?.handling_document_charge || 27500).toLocaleString('en-IN')}</td>
                  </tr>

                  {/* Down Payment Row */}
                  <tr className="down-payment-row highlighted-field">
                    <td><strong>Customer Down Payment</strong></td>
                    <td>
                      <strong>₹{parseNum(selectedVariant.quotationData?.down_payment || selectedVariant.downPayment).toLocaleString('en-IN')}</strong>
                    </td>
                  </tr>

                  <tr className="offers-row highlighted-field">
                    <td>Special Festive Offers & Discount</td>
                    <td>₹{parseNum(selectedVariant.quotationData?.offers || 0).toLocaleString('en-IN')}</td>
                  </tr>
                  <tr>
                    <td>Final Payable Down Payment</td>
                    <td>₹{parseNum(selectedVariant.quotationData?.final_down_payment || selectedVariant.downPayment).toLocaleString('en-IN')}</td>
                  </tr>

                  {/* Monthly EMI Row */}
                  <tr className="emi-row">
                    <td><strong>Monthly EMI ({selectedVariant.quotationData?.emi_years || 5} Years / {(selectedVariant.quotationData?.emi_years || 5) * 12} Months)</strong></td>
                    <td>
                      <strong>₹{Math.round(parseNum(selectedVariant.quotationData?.monthly_emi || selectedVariant.monthlyPayment)).toLocaleString('en-IN')} / month</strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Download & Action Buttons */}
          <div className="download-section">
            <div className="download-label">
              <span>Need this quotation?</span>
            </div>
            <div className="download-buttons">
              <button 
                type="button"
                className="action-btn excel-btn"
                onClick={downloadAsExcel}
                disabled={isDownloading}
                title="Download Excel Spreadsheet"
              >
                {isDownloading ? (
                  <>
                    <span className="download-spinner"></span>
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-file-excel"></i>
                    <span>Download Excel</span>
                  </>
                )}
              </button>
              
              <a 
                href={`https://wa.me/919987828417?text=Hi%20ASW%20Cars,%20I%20am%20interested%20in%20${encodeURIComponent(selectedVariant.name)}%20Quotation.`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="action-btn whatsapp-btn"
                title="Enquire on WhatsApp"
              >
                <i className="fa-brands fa-whatsapp"></i>
                <span>WhatsApp Enquiry</span>
              </a>
              
              <a 
                href="tel:+919987828690" 
                className="action-btn call-btn"
                title="Call ASW Advisor"
              >
                <i className="fa-solid fa-phone"></i>
                <span>Call Advisor</span>
              </a>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default CarDetailWithInvoices;